import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  axios.defaults.baseURL = apiBaseUrl;
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Fetch subscribers from API
      const response = await axios.get('/api/subscribers');
      const subscribers = response.data;
      
      // Find user with matching email and password
      const user = subscribers.find(
        subscriber => subscriber.email === formData.email && 
                     subscriber.password === formData.password
      );

      if (user) {
        // Store user info in localStorage or context
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('loginTime', new Date().toISOString());
        
        // Redirect to booklist page
        navigate('/booklist');
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-500 to-cyan-400 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse"></div>
      <div className="absolute top-40 right-10 w-40 h-40 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse animation-delay-2000"></div>
      <div className="absolute bottom-10 left-1/2 w-40 h-40 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse animation-delay-4000"></div>
      
      <div className="card w-full max-w-md shadow-2xl bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-3xl relative z-10 border-2 border-white/30">
        <div className="card-body p-8">
          {/* Colorful Logo/Brand Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 via-blue-400 to-cyan-400 mb-4 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent">Welcome Back</h2>
            <p className="text-gray-600 mt-2 font-medium">Sign in to your library</p>
          </div>
          
          {/* Error Alert with Gradient */}
          {error && (
            <div className="alert shadow-lg mb-4 animate-shake bg-gradient-to-r from-red-400 to-pink-400 border-0 rounded-xl">
              <div className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold">{error}</span>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <div className="form-control">
              <label className="label">
                <span className="label-text font-bold text-gray-700">Email Address</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="input input-bordered w-full pl-10 bg-white/80 border-2 border-purple-200 focus:border-purple-500 focus:bg-white focus:outline-none transition-all duration-300 rounded-lg font-medium"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-3.5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            <div className="form-control mt-5">
              <label className="label">
                <span className="label-text font-bold text-gray-700">Password</span>
              </label>
              <div className="relative">
                <input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  className="input input-bordered w-full pl-10 bg-white/80 border-2 border-blue-200 focus:border-blue-500 focus:bg-white focus:outline-none transition-all duration-300 rounded-lg font-medium"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <label className="label">
                <a href="/forgot-password" className="label-text-alt link link-hover font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent hover:from-purple-700 hover:to-cyan-700">Forgot password?</a>
              </label>
            </div>

            <div className="form-control mt-7">
              <button 
                type="submit" 
                className={`btn btn-block text-white font-bold text-lg rounded-xl bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 border-0 hover:from-purple-600 hover:via-blue-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </button>
            </div>
          </form>

          {/* Colorful Divider */}
          <div className="divider my-6 before:bg-gradient-to-r before:from-purple-300 before:to-blue-300 after:bg-gradient-to-r after:from-blue-300 after:to-cyan-300">
            <span className="text-gray-500 font-semibold">OR</span>
          </div>
          
          {/* Register Section */}
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-700 font-medium">
              Don't have an account?{' '}
              <Link to="/register" className="font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent hover:from-purple-700 hover:to-pink-700 transition-all">
                Create Account
              </Link>
            </p>
            
            {/* Subscription Badge */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-semibold text-amber-800">Annual Subscription</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-amber-900">₹250</span>
                  <span className="text-xs text-amber-700 ml-1">/year</span>
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-2 text-left">
                ✓ Unlimited access to all books & magazines
              </p>
              <Link to="/register" className="btn btn-sm btn-outline btn-warning w-full mt-3">
                Subscribe Now
              </Link>
            </div>
          </div>

        

          
        </div>
      </div>
    </div>
  );
};

export default Login;